<template>
  <v-app>
    <router-view />

    <ReleaseNote
      :model-value="showUpdateDialog && canShowUpdateDialog"
      :notes="remoteReleaseNotes"
      @update:model-value="onUpdateDialogModel"
      @refresh="refreshPortal"
    />

    <ViewedReleaseNote
      :blocked="showUpdateDialog && canShowUpdateDialog"
      @update:open="onViewedReleaseNotesOpen"
    />
  </v-app>
</template>

<script>
import "@/utils/safeJsonParse";
import { startIdleTimer } from "@/utils/idleTimer";
import { parseApiJson } from "@/utils/parseApiJson";
import ReleaseNote from "@/components/ReleaseNote.vue";
import ViewedReleaseNote from "@/components/ViewedReleaseNote.vue";
import {
  startPortalMaintenanceSubscription,
  stopPortalMaintenanceSubscription,
} from "@/utils/portalMaintenanceEvents";

const VERSION_POLL_MS = 10 * 1000;
/** Auto-reload only after this long idle while the tab is in the background */
const BACKGROUND_IDLE_AUTO_RELOAD_MS = 10 * 60 * 1000;
/** After sign-in, skip dialog briefly so login only does silent reload */
const LOGIN_DIALOG_SUPPRESS_MS = 15 * 1000;
/** Version baked into this JS bundle at CI build time */
const BAKED_BUILD_VERSION = process.env.VUE_APP_BUILD_VERSION || null;
const RUNNING_VERSION_KEY = "portalRunningBuildVersion";
const ACK_VERSION_KEY = "portalAcknowledgedBuildVersion";
const SKIP_NOTES_KEY = "portalSkipReleaseNotesOnce";

export default {
  name: "App",
  components: {
    ReleaseNote,
    ViewedReleaseNote,
  },
  data() {
    return {
      // Version of THIS loaded JS bundle (baked at build; never taken from live version.json)
      pageLoadVersion: BAKED_BUILD_VERSION || null,
      versionCheckInterval: null,
      showUpdateDialog: false,
      loginReloadInProgress: false,
      pendingUpdateDialog: false,
      /** Timestamp until which update dialog is blocked (right after login) */
      suppressUpdateDialogUntil: 0,
      remoteReleaseNotes: "",
      viewedReleaseNotesOpen: false,
    };
  },

  watch: {
    // When user logs in, always enter portal on the latest build (silent reload)
    "$store.state.currentUser": {
      handler(user, prev) {
        if (user && !prev) {
          // Only suppress while still on the login screen; session restore in the
          // portal should still show this build's release notes once.
          if (this.isLoginPage()) {
            this.suppressUpdateDialogUntil = Date.now() + LOGIN_DIALOG_SUPPRESS_MS;
            this.showUpdateDialog = false;
          }
          this.ensureLatestVersionOnLogin();
          startPortalMaintenanceSubscription({
            store: this.$store,
            router: this.$router,
          });
        }
        // Logged out → never keep update dialog open
        if (!user) {
          this.showUpdateDialog = false;
          this.pendingUpdateDialog = false;
          stopPortalMaintenanceSubscription();
        }
      },
    },
    $route(to, from) {
      // Never show update dialog on login
      if (this.isLoginRoute(to)) {
        this.showUpdateDialog = false;
      }

      // Manual test: /landing/dashboard?forceUpdate=1
      if (this.isForceUpdatePreview(to) && this.isUserLoggedIn() && !this.isLoginRoute(to)) {
        this.openUpdateDialog("forceUpdate query");
      }

      const leftLogin =
        from && this.isLoginRoute(from) && to && !this.isLoginRoute(to);
      if (leftLogin && this.isUserLoggedIn()) {
        this.markSkipReleaseNotesOnce();
        this.suppressUpdateDialogUntil = Date.now() + LOGIN_DIALOG_SUPPRESS_MS;
        this.showUpdateDialog = false;
        this.ensureLatestVersionOnLogin();
      }
    },
    showUpdateDialog: {
      handler() {
        this.syncPortalUpdateBodyClass();
      },
    },
    viewedReleaseNotesOpen: {
      handler() {
        this.syncPortalUpdateBodyClass();
      },
    },
    canShowUpdateDialog: {
      handler(visible) {
        this.syncPortalUpdateBodyClass();
        if (visible) {
          this.maybePromptReleaseNotes();
        }
      },
      immediate: true,
    },
  },

  computed: {
    canShowUpdateDialog() {
      // Dialog only while logged in and inside the portal (never on login)
      return this.isUserLoggedIn() && !this.isLoginPage();
    },
  },

  async mounted() {
    this.stopIdle = startIdleTimer(this.$router);

    // Prefer baked CI version; otherwise keep a stable per-tab baseline in sessionStorage
    // (do NOT keep re-reading live version.json as the baseline — that hides updates)
    if (BAKED_BUILD_VERSION) {
      this.pageLoadVersion = BAKED_BUILD_VERSION;
      try {
        sessionStorage.setItem(RUNNING_VERSION_KEY, BAKED_BUILD_VERSION);
      } catch (e) {
        /* ignore */
      }
      console.log("[Portal Update] Baseline version (baked):", this.pageLoadVersion);
    } else {
      let stored = null;
      try {
        stored = sessionStorage.getItem(RUNNING_VERSION_KEY);
      } catch (e) {
        stored = null;
      }
      if (stored) {
        this.pageLoadVersion = stored;
        console.log("[Portal Update] Baseline version (session):", this.pageLoadVersion);
      } else {
        this.pageLoadVersion = await this.fetchAppVersion();
        if (this.pageLoadVersion) {
          try {
            sessionStorage.setItem(RUNNING_VERSION_KEY, this.pageLoadVersion);
          } catch (e) {
            /* ignore */
          }
        }
        console.log("[Portal Update] Baseline version (fetched once):", this.pageLoadVersion);
      }
    }

    this.loadedAppBundlePath = this.getLoadedAppBundlePath();
    console.log("[Portal Update] Loaded app bundle:", this.loadedAppBundlePath);

    // Keep component `this` — do not pass unbound method to setInterval
    this.versionCheckInterval = setInterval(
      () => this.checkVersion(),
      VERSION_POLL_MS
    );
    // Tab may have been backgrounded while timers were throttled — re-check on return
    this._onVisibilityChange = () => this.onVisibilityChange();
    document.addEventListener("visibilitychange", this._onVisibilityChange);

    // Manual test hook
    if (this.isForceUpdatePreview(this.$route) && this.canShowUpdateDialog) {
      await this.fetchRemoteReleaseNotes();
      this.openUpdateDialog("forceUpdate query on mount");
    }

    // Immediate check in case a newer deploy is already live
    this.checkVersion();
    this.maybePromptReleaseNotes();
    if (this.$store?.state?.currentUser) {
      startPortalMaintenanceSubscription({
        store: this.$store,
        router: this.$router,
      });
    }
  },

  beforeUnmount() {
    stopPortalMaintenanceSubscription();
    if (this.stopIdle) this.stopIdle();
    if (this.versionCheckInterval) {
      clearInterval(this.versionCheckInterval);
      this.versionCheckInterval = null;
    }
    if (this._onVisibilityChange) {
      document.removeEventListener("visibilitychange", this._onVisibilityChange);
    }
  },
  methods: {
    syncPortalUpdateBodyClass() {
      const visible = Boolean(
        (this.showUpdateDialog && this.canShowUpdateDialog) ||
          this.viewedReleaseNotesOpen
      );
      document.body.classList.toggle("portal-update-open", visible);

      // Keep floating chats under the update overlay and non-clickable
      const floatingSelectors = [
        ".help-window",
        ".ai-chatbot-root",
        ".chat-fab",
        ".chat-window",
      ];
      document.querySelectorAll(floatingSelectors.join(", ")).forEach((el) => {
        if (!(el instanceof HTMLElement)) return;
        if (visible) {
          el.style.setProperty("z-index", "1000", "important");
          el.style.setProperty("pointer-events", "none", "important");
        } else {
          el.style.removeProperty("z-index");
          el.style.removeProperty("pointer-events");
        }
      });
    },
    parseReleaseNotesDocument(raw) {
      if (raw == null || raw === "") return null;
      const text = typeof raw === "string" ? raw.trim() : "";
      if (!text) return null;
      if (!text.includes(":::highlight") && !text.includes("## ")) return null;

      const lines = text.split(/\r?\n/);
      let index = 0;
      const title = (lines[index] || "").trim() || "RCS Platform Release Notes";
      index += 1;

      let date = "";
      if (
        lines[index] &&
        !lines[index].trim().startsWith(":::") &&
        !lines[index].trim().startsWith("##")
      ) {
        date = lines[index].trim();
        index += 1;
      }

      let highlight = null;
      const sections = [];
      let currentSection = null;

      const startSection = (name) => {
        currentSection = { title: name, items: [] };
        sections.push(currentSection);
      };

      const splitItem = (line) => {
        const cleaned = line
          .replace(/^[-*•]\s+/, "")
          .replace(/^\d+\.\s+/, "")
          .trim();
        const dash = cleaned.indexOf(" — ");
        if (dash > 0) {
          return {
            label: cleaned.slice(0, dash).trim(),
            text: cleaned.slice(dash + 3).trim(),
            hasDash: true,
          };
        }
        const prefix = cleaned.match(/^(Fixed|Added):\s+(.*)$/i);
        if (prefix) {
          return {
            label: `${prefix[1]}:`,
            text: prefix[2],
            hasDash: false,
          };
        }
        return { label: "", text: cleaned, hasDash: false };
      };

      while (index < lines.length) {
        const trimmed = (lines[index] || "").trim();
        if (!trimmed) {
          index += 1;
          continue;
        }

        if (trimmed === ":::highlight") {
          index += 1;
          const highlightTitle = (lines[index] || "").trim();
          index += 1;
          const paragraphs = [];
          let buffer = [];
          const flush = () => {
            const paragraph = buffer.join(" ").trim();
            if (paragraph) paragraphs.push(paragraph);
            buffer = [];
          };
          while (index < lines.length && lines[index].trim() !== ":::") {
            if (!lines[index].trim()) flush();
            else buffer.push(lines[index].trim());
            index += 1;
          }
          flush();
          highlight = { title: highlightTitle, paragraphs };
          if (lines[index] && lines[index].trim() === ":::") index += 1;
          continue;
        }

        if (trimmed.startsWith("## ")) {
          startSection(trimmed.slice(3).trim());
          index += 1;
          continue;
        }

        if (
          trimmed.startsWith("- ") ||
          trimmed.startsWith("* ") ||
          trimmed.startsWith("• ")
        ) {
          if (!currentSection) startSection("");
          currentSection.items.push(splitItem(trimmed));
          index += 1;
          continue;
        }

        index += 1;
      }

      if (!highlight && !sections.length) return null;
      return { title, date, highlight, sections };
    },
    formatNoteHtml(text) {
      const escaped = String(text || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
      return escaped.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
    },
    parseReleaseNotes(raw) {
      if (raw == null || raw === "") return [];

      if (Array.isArray(raw)) {
        return raw
          .map((item) => String(item ?? "").trim())
          .filter(Boolean);
      }

      if (typeof raw === "object") {
        return Object.values(raw)
          .map((item) => String(item ?? "").trim())
          .filter(Boolean);
      }

      if (typeof raw !== "string") {
        return [String(raw).trim()].filter(Boolean);
      }

      const trimmed = raw.trim();
      if (!trimmed) return [];

      // JSON array / object string from get_current_user
      if (trimmed[0] === "[" || trimmed[0] === "{") {
        const parsed = parseApiJson(trimmed, null);
        if (parsed != null) {
          return this.parseReleaseNotes(parsed);
        }
      }
//yellema
      // Keep each line exactly as entered in release notes
      return trimmed
        .split(/\r?\n+/)
        .map((line) => String(line || "").trim())
        .filter(Boolean);
    },
    /** Resolve app-root relative asset (respects Vue publicPath / BASE_URL). */
    appAssetUrl(fileName) {
      const base = process.env.BASE_URL || "/";
      const normalizedBase = base.endsWith("/") ? base : `${base}/`;
      return `${normalizedBase}${fileName}`;
    },
    async fetchRemoteReleaseNotes() {
      try {
        const url = `${this.appAssetUrl("ReleaseNotes.txt")}?t=${Date.now()}`;
        const res = await fetch(url, {
          cache: "no-store",
          headers: {
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
          },
        });
        if (!res.ok) return;

        const text = await res.text();
        const trimmed = (text || "").trim();
        if (
          !trimmed ||
          trimmed.toLowerCase().startsWith("<!doctype") ||
          trimmed.toLowerCase().startsWith("<html")
        ) {
          return;
        }

        this.remoteReleaseNotes = trimmed;
      } catch (error) {
        console.warn("[Release notes] ReleaseNotes.txt fetch failed:", error);
      }
    },
    async fetchAppVersion() {
      try {
        const url = `${this.appAssetUrl("version.json")}?t=${Date.now()}`;
        const res = await fetch(url, {
          cache: "no-store",
          headers: {
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
          },
        });
        if (!res.ok) {
          console.warn("[Portal Update] version.json HTTP", res.status, url);
          return null;
        }

        const text = await res.text();
        const trimmed = (text || "").trim();
        // Skip HTML SPA fallbacks; accept JSON even with odd content-types
        if (!trimmed || (trimmed[0] !== "{" && trimmed[0] !== "[")) {
          console.warn("[Portal Update] version.json not JSON", url);
          return null;
        }

        const data = parseApiJson(trimmed, null);
        return data?.version ?? null;
      } catch (error) {
        console.warn("[Portal Update] version.json fetch failed:", error);
        return null;
      }
    },
    isUserLoggedIn() {
      return Boolean(this.$store?.state?.currentUser);
    },
    getAcknowledgedVersion() {
      try {
        return localStorage.getItem(ACK_VERSION_KEY);
      } catch (e) {
        return null;
      }
    },
    setAcknowledgedVersion(version) {
      if (!version) return;
      try {
        localStorage.setItem(ACK_VERSION_KEY, version);
      } catch (e) {
        /* ignore */
      }
    },
    markSkipReleaseNotesOnce() {
      try {
        sessionStorage.setItem(SKIP_NOTES_KEY, "1");
      } catch (e) {
        /* ignore */
      }
    },
    consumeSkipReleaseNotesOnce() {
      try {
        const skip = sessionStorage.getItem(SKIP_NOTES_KEY) === "1";
        if (skip) sessionStorage.removeItem(SKIP_NOTES_KEY);
        return skip;
      } catch (e) {
        return false;
      }
    },
    async maybePromptReleaseNotes() {
      if (this._releaseNotesPrompted) return;
      if (!this.canShowUpdateDialog) return;

      if (this.isForceUpdatePreview()) {
        this._releaseNotesPrompted = true;
        await this.fetchRemoteReleaseNotes();
        this.openUpdateDialog("forceUpdate query");
        return;
      }

      // Just signed in / idle auto-refresh: stay on the new build with no popup
      if (this.consumeSkipReleaseNotesOnce()) {
        this._releaseNotesPrompted = true;
        this.setAcknowledgedVersion(this.pageLoadVersion);
        return;
      }

      const current = this.pageLoadVersion;
      if (!current) return;
      if (this.getAcknowledgedVersion() === current) {
        this._releaseNotesPrompted = true;
        return;
      }

      this._releaseNotesPrompted = true;
      await this.fetchRemoteReleaseNotes();
      this.openUpdateDialog("new build loaded");
    },
    isLoginRoute(route) {
      const path = route?.path || "/";
      const name = route?.name;
      return path === "/" || path === "/login" || name === "Login";
    },
    isLoginPage() {
      return this.isLoginRoute(this.$route);
    },
    isForceUpdatePreview(route = this.$route) {
      const q = route?.query || {};
      return q.forceUpdate === "1" || q.forceUpdate === "true";
    },
    /** Tab/window is open and visible to the user (not minimized / other tab). */
    isAppInForeground() {
      return (
        typeof document !== "undefined" &&
        document.visibilityState === "visible" &&
        !document.hidden
      );
    },
    /** True if there has been no interaction for at least `ms`. */
    isUserIdleFor(ms) {
      const lastActive = Number(localStorage.getItem("lastActiveTime") || 0);
      // No activity stamp yet → treat as idle (safe for background auto-refresh)
      if (!lastActive) return true;
      return Date.now() - lastActive >= ms;
    },
    isUpdateDialogSuppressed() {
      return Date.now() < Number(this.suppressUpdateDialogUntil || 0);
    },
    getLoadedAppBundlePath() {
      try {
        const el = document.querySelector('script[src*="/js/app."]');
        const src = el?.getAttribute("src") || el?.src || "";
        const match = String(src).match(/\/js\/app\.[a-z0-9]+\.js/i);
        return match ? match[0] : null;
      } catch (e) {
        return null;
      }
    },
    async fetchDeployedAppBundlePath() {
      try {
        const url = `${this.appAssetUrl("")}?t=${Date.now()}`;
        const res = await fetch(url, {
          cache: "no-store",
          headers: {
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
            Accept: "text/html",
          },
        });
        if (!res.ok) return null;
        const html = await res.text();
        const match = String(html || "").match(/\/js\/app\.[a-z0-9]+\.js/i);
        return match ? match[0] : null;
      } catch (e) {
        return null;
      }
    },
    openUpdateDialog(reason = "") {
      if (!this.canShowUpdateDialog) {
        this.pendingUpdateDialog = true;
        console.log("[Portal Update] Queued dialog — cannot show yet", reason);
        return;
      }
      if (this.isUpdateDialogSuppressed()) {
        this.pendingUpdateDialog = true;
        console.log("[Portal Update] Queued dialog — login suppress active", reason);
        return;
      }
      this.pendingUpdateDialog = false;
      this.showUpdateDialog = true;
      console.log("[Portal Update] Dialog opened:", reason);
    },
    onVisibilityChange() {
      // Returning to this tab → re-check (will show dialog if a new build exists)
      if (document.visibilityState === "visible") {
        this.checkVersion();
      } else {
        // Just went to background — still poll path handles idle auto-refresh
        this.checkVersion();
      }
    },
    onViewedReleaseNotesOpen(open) {
      this.viewedReleaseNotesOpen = Boolean(open);
    },
    onUpdateDialogModel(val) {
      // Block opening on login / logged-out; allow close always
      if (!val) {
        if (this.pendingUpdateDialog && this.canShowUpdateDialog) {
          this.showUpdateDialog = true;
          return;
        }
        this.showUpdateDialog = false;
        return;
      }
      if (this.canShowUpdateDialog && !this.isUpdateDialogSuppressed()) {
        this.showUpdateDialog = true;
      }
    },
    /**
     * After sign-in / entering portal: if a newer build exists, hard-reload
     * so the user starts on the latest version (no dialog on login).
     */
    async ensureLatestVersionOnLogin() {
      if (this.loginReloadInProgress) return;
      // Never show dialog on login / just-logged-in path
      this.showUpdateDialog = false;
      if (this.isLoginPage() || !this.isUserLoggedIn()) {
        return;
      }

      const latest = await this.fetchAppVersion();
      if (!latest) return;

      // Do not overwrite a baked/session baseline with live version.json
      if (!this.pageLoadVersion) {
        this.pageLoadVersion = latest;
        try {
          sessionStorage.setItem(RUNNING_VERSION_KEY, latest);
        } catch (e) {
          /* ignore */
        }
        return;
      }

      if (latest !== this.pageLoadVersion) {
        console.log(
          "[Portal Update] Silent refresh on login — new version detected",
          { from: this.pageLoadVersion, to: latest }
        );
        this.markSkipReleaseNotesOnce();
        this.loginReloadInProgress = true;
        this.showUpdateDialog = false;
        try {
          sessionStorage.removeItem(RUNNING_VERSION_KEY);
        } catch (e) {
          /* ignore */
        }
        window.location.reload();
      }
    },
    /**
     * Poll for a new deploy:
     * 1) Login page / just logged in → never show dialog (silent reload on login)
     * 2) Portal tab open (visible), user working or not → show refresh dialog
     * 3) Portal in background + idle 10+ min → auto refresh (no dialog)
     * 4) Portal in background but not idle yet → wait
     */
    async checkVersion() {
      try {
        // Flush dialog queued during login suppress
        if (
          this.pendingUpdateDialog &&
          this.canShowUpdateDialog &&
          !this.isUpdateDialogSuppressed() &&
          !this.loginReloadInProgress
        ) {
          await this.fetchRemoteReleaseNotes();
          this.openUpdateDialog("pending after suppress");
        }

        const nextVersion = await this.fetchAppVersion();
        const deployedBundle = await this.fetchDeployedAppBundlePath();

        // Local fallback only: establish baseline if bake + first fetch both missed
        if (this.pageLoadVersion == null) {
          if (nextVersion != null) {
            this.pageLoadVersion = nextVersion;
            try {
              sessionStorage.setItem(RUNNING_VERSION_KEY, nextVersion);
            } catch (e) {
              /* ignore */
            }
            console.log("[Portal Update] Baseline set from poll:", nextVersion);
          }
          return;
        }

        const versionChanged =
          nextVersion != null && nextVersion !== this.pageLoadVersion;
        const bundleChanged =
          !!deployedBundle &&
          !!this.loadedAppBundlePath &&
          deployedBundle !== this.loadedAppBundlePath;

        if (!versionChanged && !bundleChanged) {
          return;
        }

        console.log("[Portal Update] Update detected", {
          runningVersion: this.pageLoadVersion,
          latestVersion: nextVersion,
          loadedBundle: this.loadedAppBundlePath,
          deployedBundle,
          versionChanged,
          bundleChanged,
          loggedIn: this.isUserLoggedIn(),
          onLogin: this.isLoginPage(),
          foreground: this.isAppInForeground(),
          suppressed: this.isUpdateDialogSuppressed(),
        });

        const loggedIn = this.isUserLoggedIn();
        const onLoginPage = this.isLoginPage();

        // 1) Never show on login page, logged out, or during login silent-reload window
        if (onLoginPage || !loggedIn || this.loginReloadInProgress) {
          this.showUpdateDialog = false;
          return;
        }

      // User is on this tab (working, or just switched back) → show refresh dialog
      if (this.isAppInForeground()) {
        console.log(
          "[Portal Update] Refresh dialog shown — user is on the portal tab",
          { from: this.pageLoadVersion, to: nextVersion }
        );
        this.showUpdateDialog = true;
        this.fetchRemoteReleaseNotes();
        return;
      }

        // 3) Background + idle 10+ min (not using the portal) → auto refresh
        if (this.isUserIdleFor(BACKGROUND_IDLE_AUTO_RELOAD_MS)) {
          console.log(
            "[Portal Update] Auto refresh — background and idle 10+ min",
            { from: this.pageLoadVersion, to: nextVersion }
          );
          this.markSkipReleaseNotesOnce();
          try {
            sessionStorage.removeItem(RUNNING_VERSION_KEY);
          } catch (e) {
            /* ignore */
          }
          window.location.reload();
          return;
        }

        // 4) Background but still recently active → wait for idle or tab return
        this.pendingUpdateDialog = true;
        console.log(
          "[Portal Update] Waiting — background but not idle 10 min yet",
          { from: this.pageLoadVersion, to: nextVersion }
        );
      } catch (error) {
        console.error("[Portal Update] Version check failed:", error);
      }
    },
    async refreshPortal() {
      console.log("[Portal Update] Manual refresh — user clicked REFRESH");
      const latest = await this.fetchAppVersion();
      this.setAcknowledgedVersion(latest || this.pageLoadVersion);
      if (this.pageLoadVersion && latest && this.pageLoadVersion === latest) {
        this.showUpdateDialog = false;
        return;
      }
      try {
        sessionStorage.removeItem(RUNNING_VERSION_KEY);
      } catch (e) {
        /* ignore */
      }
      window.location.reload();
    },
  },
};
</script>

<style>
/* =========================================================
   GLOBAL RESET & TYPOGRAPHY
   ========================================================= */
* {
  box-sizing: border-box;
  font-family: Sora, Helvetica, Arial, sans-serif;
}

html,
body {
  margin: 0;
  padding: 0;
  height: 100%;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

#app {
  min-height: 100vh;
}

/* =========================================================
   VUETIFY TABLE BASE
   ========================================================= */
.v-table {
  font-size: 14px;
}

.v-table thead th {
  background-color: rgba(0, 102, 204, 0.1);
  color: rgb(0, 102, 204);
  font-weight: 600;
}

/* =========================================================
   DATA TABLE FOOTER CLEANUP
   ========================================================= */
.custom-data-table:not(.show-items-per-page)
  .v-data-table-footer__items-per-page,
.custom-data-table:not(.show-items-per-page) .v-data-table-footer__select {
  display: none !important;
}

.custom-data-table:not(.show-items-per-page) .v-data-table-footer__pagination {
  margin-left: auto !important;
}

/* Items-per-page select wide enough for values like 100 / 200 / All */
.custom-data-table.show-items-per-page .v-data-table-footer__items-per-page {
  flex: 0 0 auto;
}

.custom-data-table.show-items-per-page .v-data-table-footer__items-per-page .v-select,
.custom-data-table.show-items-per-page .v-data-table-footer__select {
  min-width: 72px !important;
  max-width: none !important;
}

.custom-data-table.show-items-per-page .v-data-table-footer__items-per-page .v-field,
.custom-data-table.show-items-per-page .v-data-table-footer__select .v-field {
  min-width: 72px !important;
}

.custom-data-table.show-items-per-page .v-data-table-footer__items-per-page .v-select__selection-text,
.custom-data-table.show-items-per-page .v-data-table-footer__select .v-select__selection-text {
  overflow: visible !important;
  text-overflow: clip !important;
  white-space: nowrap !important;
}

/* =========================================================
   TABLE LAYOUT (NO SCROLL OWNER HERE)
   ========================================================= */
.table-container {
  width: 100%;
}

.table-container td,
.table-container th {
  white-space: nowrap;
}

/* =========================================================
   ✅ VUETIFY 3 TABLE SCROLL OWNER
   ========================================================= */
.custom-data-table .v-table__wrapper {
  overflow-x: auto;
  overflow-y: auto;

  /* Firefox */
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.35) transparent;
}

/* =========================================================
   THIN TABLE SCROLLBAR
   ========================================================= */

/* Chrome / Edge / Safari */
.custom-data-table .v-table__wrapper::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.custom-data-table .v-table__wrapper::-webkit-scrollbar-track {
  background: transparent;
}

.custom-data-table .v-table__wrapper::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.25);
  border-radius: 4px;
}

.custom-data-table .v-table__wrapper::-webkit-scrollbar-thumb:hover {
  background-color: rgba(0, 0, 0, 0.4);
}

/* =========================================================
   GLOBAL CONTENT SCROLLBAR (CARDS / PAGES)
   ========================================================= */

/* Chrome / Edge / Safari */
.v-main::-webkit-scrollbar,
.v-container::-webkit-scrollbar,
.v-card::-webkit-scrollbar {
  width: 6px;
}

.v-main::-webkit-scrollbar-track,
.v-container::-webkit-scrollbar-track,
.v-card::-webkit-scrollbar-track {
  background: transparent;
}

.v-main::-webkit-scrollbar-thumb,
.v-container::-webkit-scrollbar-thumb,
.v-card::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.25);
  border-radius: 6px;
}

.v-main::-webkit-scrollbar-thumb:hover,
.v-container::-webkit-scrollbar-thumb:hover,
.v-card::-webkit-scrollbar-thumb:hover {
  background-color: rgba(0, 0, 0, 0.4);
}

/* Firefox */
.v-main,
.v-container,
.v-card {
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.35) transparent;
}

/* =========================================================
   PORTAL UPDATE DIALOG
   ========================================================= */
/* Must sit above Support Assistant (10000) and AI ChatBot (9000) */
.v-overlay:has(.portal-update-dialog),
.v-overlay:has(.portal-update-card) {
  z-index: 100000 !important;
}

body.portal-update-open .help-window,
body.portal-update-open .ai-chatbot-root,
body.portal-update-open .chat-fab,
body.portal-update-open .chat-window {
  z-index: 1000 !important;
  pointer-events: none !important;
}

.portal-update-dialog {
  overflow: visible !important;
  z-index: 100000 !important;
}

.portal-update-card {
  overflow: hidden;
  background: #ffffff !important;
  border-radius: 16px !important;
  display: flex;
  flex-direction: column;
  max-height: min(92vh, 860px);
}

.portal-update-header {
  flex-shrink: 0;
  background: #0b1f3a;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 32px;
  min-height: 84px;
}

.portal-update-logo {
  height: 64px;
  width: auto;
  max-width: 320px;
  object-fit: contain;
  mix-blend-mode: screen;
}

.portal-update-body {
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  text-align: left;
  padding: 0;
  background: #ffffff;
  overflow: hidden;
}

.portal-update-content {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 28px 36px 12px;
  text-align: left;
  scrollbar-width: thin;
  scrollbar-color: rgba(11, 31, 58, 0.28) transparent;
}

.portal-update-content::-webkit-scrollbar {
  width: 6px;
}

.portal-update-content::-webkit-scrollbar-track {
  background: transparent;
}

.portal-update-content::-webkit-scrollbar-thumb {
  background-color: rgba(0, 102, 204, 0.3);
  border-radius: 6px;
}

.portal-update-content::-webkit-scrollbar-thumb:hover {
  background-color: rgba(0, 102, 204, 0.5);
}

.portal-update-footer {
  position: sticky;
  bottom: 0;
  z-index: 2;
  flex-shrink: 0;
  padding: 12px 36px 22px;
  background: #ffffff;
  border-top: 1px solid rgba(11, 31, 58, 0.08);
}

.release-notes-doc {
  color: #111111;
}

.rn-title {
  margin: 0;
  color: #0b1f3a;
  font-size: 1.85rem;
  font-weight: 800;
  line-height: 1.2;
  letter-spacing: 0.01em;
}

.rn-date {
  margin: 8px 0 20px;
  color: #a68244;
  font-size: 1.05rem;
  font-weight: 600;
}

.rn-highlight {
  background: #fcf4e7;
  border: 1px solid #d8bc8a;
  border-radius: 4px;
  padding: 16px 18px 14px;
  margin: 0 0 28px;
}

.rn-highlight-title {
  margin: 0 0 10px;
  color: #0b1f3a;
  font-size: 1.05rem;
  font-weight: 800;
}

.rn-highlight-text {
  margin: 0 0 10px;
  color: #111111;
  font-size: 0.95rem;
  line-height: 1.55;
}

.rn-highlight-text:last-child {
  margin-bottom: 0;
}

.rn-highlight-text :deep(strong) {
  font-weight: 700;
}

.rn-section {
  margin: 0 0 22px;
}

.rn-section-title {
  margin: 0 0 10px;
  padding-bottom: 8px;
  color: #0b1f3a;
  font-size: 1.12rem;
  font-weight: 800;
  border-bottom: 1px solid #0b1f3a;
}

.rn-list {
  margin: 0;
  padding: 6px 0 0;
  list-style: none;
}

.rn-item {
  display: grid;
  grid-template-columns: 10px 12.5rem 1.2rem minmax(0, 1fr);
  column-gap: 8px;
  align-items: start;
  margin: 0 0 14px;
  color: #0b1f3a;
  font-size: 0.95rem;
  line-height: 1.5;
}

.rn-item::before {
  content: "";
  width: 7px;
  height: 7px;
  margin-top: 0.52em;
  border-radius: 50%;
  background: #c4a35a;
}

.rn-item-label {
  font-weight: 700;
  color: #0b1f3a;
}

.rn-item-dash {
  color: #0b1f3a;
  text-align: center;
  font-weight: 400;
}

.rn-item-text {
  color: #0b1f3a;
  font-weight: 400;
}

.rn-item--plain .rn-item-text,
.rn-item--inline .rn-item-text {
  grid-column: 2 / -1;
}

.portal-update-message {
  margin: 0 auto 12px;
  color: #4a5568;
  font-size: 0.88rem;
  line-height: 1.45;
  text-align: center;
}

.portal-update-btn {
  position: relative;
  z-index: 1;
  width: 100%;
  margin: 0 auto;
  height: 48px !important;
  font-weight: 700 !important;
  letter-spacing: 0.08em !important;
  color: #ffffff !important;
  border-radius: 12px !important;
  box-shadow: 0 10px 22px rgba(0, 102, 204, 0.35) !important;
}
</style>
